import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { useLanguage } from '../../i18n/LanguageContext';
import {
  ProgramApiService,
  TrainingProgram,
  Enrollment,
} from '../../services/api/ProgramApiService';
import { ProgramBrowserCard } from './ProgramBrowserCard';
import { EnrollmentSetupDialog } from './EnrollmentSetupDialog';
import { ActiveEnrollmentPanel } from './ActiveEnrollmentPanel';

export const TrainingProgramsView: React.FC = () => {
  const { t } = useLanguage();
  const [programs, setPrograms] = useState<TrainingProgram[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrollTarget, setEnrollTarget] = useState<TrainingProgram | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [progs, enrs] = await Promise.all([
        ProgramApiService.getPrograms(),
        ProgramApiService.getEnrollments(),
      ]);
      setPrograms(progs);
      setEnrollments(enrs);
    } catch {
      setError('Failed to load programs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeEnrollments = enrollments.filter(
    (e) => e.status === 'active' || e.status === 'paused'
  );

  const enrolledProgramIds = new Set(enrollments.map((e) => e.program_id));
  const availablePrograms = programs.filter((p) => !enrolledProgramIds.has(p.id));

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('programs.title')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Active enrollments */}
          {activeEnrollments.length > 0 ? (
            activeEnrollments.map((enrollment) => (
              <Box key={enrollment.id} sx={{ mb: 4 }}>
                <ActiveEnrollmentPanel
                  enrollment={enrollment}
                  onEnrollmentChanged={load}
                />
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('programs.noEnrollment')}
            </Typography>
          )}

          {/* Program browser */}
          {availablePrograms.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('programs.browse')}
              </Typography>
              <Grid container spacing={2}>
                {availablePrograms.map((program) => (
                  <Grid key={program.id} size={{ xs: 12, sm: 6, md: 4 }}>
                    <ProgramBrowserCard
                      program={program}
                      onEnroll={(p) => setEnrollTarget(p)}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </>
      )}

      <EnrollmentSetupDialog
        open={!!enrollTarget}
        program={enrollTarget}
        onClose={() => setEnrollTarget(null)}
        onEnrolled={() => {
          setEnrollTarget(null);
          load();
        }}
      />
    </Box>
  );
};
